import React, { useState } from 'react';
import {
  Input,
  Button,
  Space,
  Row,
  Col,
  Typography,
  message,
  Table,
  Card,
  Tag,
  Empty,
  Spin,
  Radio,
  Descriptions
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  SettingOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import API from '../services/api';

const { Title, Text } = Typography;

const AdminApplyRuleQuery = () => {
  const [startIp, setStartIp] = useState('');
  const [loading, setLoading] = useState(false);
  const [applyRuleConfig, setApplyRuleConfig] = useState(null);
  const [hasQueried, setHasQueried] = useState(false);
  const [queryType, setQueryType] = useState('firewall'); // 'firewall' 或 'cc'

  // IP地址输入框变化处理
  const handleIpChange = (e) => {
    setStartIp(e.target.value);
  };

  // IP地址格式验证
  const validateIpFormat = (ip) => {
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip);
  };

  // 查询应用规则配置
  const handleQueryApplyRuleConfig = async () => {
    if (!startIp.trim()) {
      message.warning('请输入起始IP地址');
      return;
    }

    if (!validateIpFormat(startIp.trim())) {
      message.error('请输入有效的IP地址格式');
      return;
    }

    setLoading(true);
    try {
      // 根据查询类型选择不同的API接口
      const apiEndpoint = queryType === 'cc' ? '/api/get-apply-rule-config-cc' : '/api/get-apply-rule-config';
      const response = await API.post(apiEndpoint, {
        start_ip: startIp.trim()
      });

      // 处理响应数据
      if (response.data) {
        const { data, msg } = response.data;
        setApplyRuleConfig(data);
        setHasQueried(true);
        const queryTypeName = queryType === 'cc' ? 'CC墙' : '大墙';
        message.success(msg || `查询${queryTypeName}应用规则配置成功`);
      } else {
        message.error('查询失败，未返回有效数据');
        setApplyRuleConfig(null);
      }
    } catch (error) {
      console.error('查询应用规则配置失败:', error);
      
      const queryTypeName = queryType === 'cc' ? 'CC墙' : '大墙';
      
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (error.response?.status === 403) {
        message.error('没有管理员权限');
      } else if (error.response?.status === 400) {
        message.error('start_ip参数不能为空');
      } else {
        message.error(`查询${queryTypeName}应用规则配置时出错`);
      }
      
      setApplyRuleConfig(null);
      setHasQueried(true);
    } finally {
      setLoading(false);
    }
  };

  // 重置查询
  const handleReset = () => {
    setStartIp('');
    setApplyRuleConfig(null);
    setHasQueried(false);
  };

  // 查询类型改变处理
  const handleQueryTypeChange = (e) => {
    setQueryType(e.target.value);
    // 如果已经查询过，清空结果
    if (hasQueried) {
      setApplyRuleConfig(null);
      setHasQueried(false);
    }
  };

  // 渲染防护规则集列表
  const renderDefendRuleSets = (defendRuleSets) => {
    if (!defendRuleSets || defendRuleSets.length === 0) {
      return <Text type="secondary">无防护规则集</Text>;
    }

    const columns = [
      {
        title: '规则集名称',
        dataIndex: 'set_name',
        key: 'set_name',
        width: '50%',
        ellipsis: true,
        render: (text) => (
          <Text strong style={{ color: '#1890ff' }}>
            {text}
          </Text>
        )
      },
      {
        title: '规则集ID',
        dataIndex: 'set_id',
        key: 'set_id',
        width: '30%',
        render: (text) => <Text code>{text}</Text>
      },
      {
        title: '端口范围',
        dataIndex: 'ports',
        key: 'ports',
        width: '20%',
        render: (ports) => {
          if (!ports || ports.length === 0) {
            return <Text type="secondary">-</Text>;
          }
          return (
            <div>
              {ports.map((port, index) => {
                const [start, end] = port.split(',');
                const portText = start === '0' && end === '0' ? '全部端口' : 
                                start === end ? start : `${start}-${end}`;
                return (
                  <Tag key={index} color="blue">
                    {portText}
                  </Tag>
                );
              })}
            </div>
          );
        }
      }
    ];

    return (
      <Table
        columns={columns}
        dataSource={defendRuleSets}
        rowKey={(record, index) => record.set_id || index}
        pagination={false}
        size="small"
        tableLayout="fixed"
      />
    );
  };

  // 渲染应用规则列表
  const renderApplyRuleList = (rules) => {
    if (!rules || rules.length === 0) {
      return <Text type="secondary">无应用规则</Text>;
    }

    const columns = [
      {
        title: '规则ID',
        dataIndex: 'rule_id',
        key: 'rule_id',
        width: '8%',
        render: (text) => <Text code>#{text}</Text>
      },
      {
        title: '规则名称',
        dataIndex: 'rule_name',
        key: 'rule_name',
        width: '20%',
        ellipsis: true,
        render: (text) => (
          <Text strong style={{ color: '#1890ff' }}>
            {text || '未命名规则'}
          </Text>
        )
      },
      {
        title: 'IP范围',
        key: 'ip_range',
        width: '15%',
        render: (record) => (
          <div>
            <Text code>{record.start_ip}</Text>
            <br />
            <Text code>{record.end_ip}</Text>
          </div>
        )
      },
      {
        title: '直通状态',
        dataIndex: 'pass_through',
        key: 'pass_through',
        width: '8%',
        render: (value) => (
          <Tag color={value === 1 ? 'success' : 'default'}>
            {value === 1 ? '直通' : '防护'}
          </Tag>
        )
      },
      {
        title: '全局过滤模块',
        key: 'global_filter',
        width: '15%',
        render: (record) => (
          <div>
            {record.tcp_check === 1 && <Tag color="blue">TCP检查</Tag>}
            {record.udp === 1 && <Tag color="orange">UDP</Tag>}
            {record.icmp === 1 && <Tag color="green">ICMP</Tag>}
            {record.other === 1 && <Tag color="purple">其他协议</Tag>}
            {record.syn_flood === 1 && <Tag color="volcano">SYN Flood</Tag>}
            {record.attack_detection === 1 && <Tag color="red">攻击检测</Tag>}
            {record.sample_library_filter === 1 && <Tag color="cyan">样本库过滤</Tag>}
            {record.trusted_source_detection === 1 && <Tag color="lime">可信源检测</Tag>}
          </div>
        )
      },
      {
        title: 'SYN算法模式',
        key: 'syn_algorithm',
        width: '10%',
        render: (record) => (
          <div>
            {record.syn_algorithm_one === 1 && <Tag color="processing">模式一</Tag>}
            {record.syn_algorithm_two === 1 && <Tag color="processing">模式二</Tag>}
            {record.syn_algorithm_three === 1 && <Tag color="processing">模式三</Tag>}
            {!record.syn_algorithm_one && !record.syn_algorithm_two && !record.syn_algorithm_three && (
              <Text type="secondary">未启用</Text>
            )}
          </div>
        )
      },
      {
        title: '流量限制(Mbps)',
        key: 'traffic_limit',
        width: '12%',
        render: (record) => (
          <div>
            <Text type="secondary" style={{ fontSize: '12px' }}>入站:</Text>
            <br />
            <Text>
              {record.traffic_input_limit_per_second === -1 ? '系统限制' : 
               record.traffic_input_limit_per_second === 0 ? '不限制' : 
               `${record.traffic_input_limit_per_second}Mbps`}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>出站:</Text>
            <br />
            <Text>
              {record.traffic_output_limit_per_second === -1 ? '系统限制' : 
               record.traffic_output_limit_per_second === 0 ? '不限制' : 
               `${record.traffic_output_limit_per_second}Mbps`}
            </Text>
          </div>
        )
      },
      {
        title: '系统插件',
        key: 'system_plugins',
        width: '10%',
        render: (record) => (
          <div>
            {record.domainfilter === 1 && <Tag color="geekblue">域名过滤</Tag>}
            {record.domainfilter !== 1 && <Text type="secondary">未启用</Text>}
          </div>
        )
      },
      {
        title: '防护规则集',
        dataIndex: 'defend_rule_sets',
        key: 'defend_rule_sets',
        width: '18%',
        render: (defendRuleSets) => {
          if (!defendRuleSets || defendRuleSets.length === 0) {
            return <Text type="secondary">无</Text>;
          }
          return (
            <div>
              {defendRuleSets.slice(0, 2).map((ruleSet, index) => (
                <div key={index} style={{ marginBottom: '4px' }}>
                  <Tag color="processing" style={{ fontSize: '11px', marginBottom: '2px' }}>
                    {ruleSet.set_name}
                  </Tag>
                </div>
              ))}
              {defendRuleSets.length > 2 && (
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  +{defendRuleSets.length - 2} 个规则集
                </Text>
              )}
            </div>
          );
        }
      }
    ];

    return (
      <Table
        columns={columns}
        dataSource={rules}
        rowKey={(record, index) => record._id || record.rule_id || index}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `第 ${range[0]}-${range[1]} 条，共 ${total} 条规则`,
        }}
        size="middle"
        tableLayout="fixed"
                 expandable={{
           expandedRowRender: (record) => (
             <div style={{ padding: '16px', background: '#fafafa' }}>
               {/* 详细配置信息 */}
               <div style={{ marginBottom: '16px' }}>
                 <Title level={5} style={{ marginBottom: '12px' }}>
                   详细配置信息
                 </Title>
                 <Descriptions size="small" bordered column={3}>
                   <Descriptions.Item label="数据库ID">
                     <Text code>{record._id}</Text>
                   </Descriptions.Item>
                   <Descriptions.Item label="应用规则ID">
                     <Text code>{record.rule_id}</Text>
                     <Text type="secondary" style={{ fontSize: '11px', marginLeft: '8px' }}>
                       (用于删除或编辑)
                     </Text>
                   </Descriptions.Item>
                   <Descriptions.Item label="直通模式">
                     <Tag color={record.pass_through === 1 ? 'success' : 'default'}>
                       {record.pass_through === 1 ? '是 - 流量直通，不经过防护' : '否 - 流量经过防护处理'}
                     </Tag>
                   </Descriptions.Item>
                   
                   {/* 全局过滤模块详情 */}
                   <Descriptions.Item label="TCP检查" span={1}>
                     <Tag color={record.tcp_check === 1 ? 'blue' : 'default'}>
                       {record.tcp_check === 1 ? '已启用' : '未启用'}
                     </Tag>
                   </Descriptions.Item>
                   <Descriptions.Item label="UDP过滤" span={1}>
                     <Tag color={record.udp === 1 ? 'orange' : 'default'}>
                       {record.udp === 1 ? '已启用' : '未启用'}
                     </Tag>
                   </Descriptions.Item>
                   <Descriptions.Item label="ICMP过滤" span={1}>
                     <Tag color={record.icmp === 1 ? 'green' : 'default'}>
                       {record.icmp === 1 ? '已启用' : '未启用'}
                     </Tag>
                   </Descriptions.Item>
                   
                   <Descriptions.Item label="其他协议过滤" span={1}>
                     <Tag color={record.other === 1 ? 'purple' : 'default'}>
                       {record.other === 1 ? '已启用' : '未启用'}
                     </Tag>
                   </Descriptions.Item>
                   <Descriptions.Item label="可信源检测" span={1}>
                     <Tag color={record.trusted_source_detection === 1 ? 'lime' : 'default'}>
                       {record.trusted_source_detection === 1 ? '已启用' : '未启用'}
                     </Tag>
                   </Descriptions.Item>
                   <Descriptions.Item label="攻击检测" span={1}>
                     <Tag color={record.attack_detection === 1 ? 'red' : 'default'}>
                       {record.attack_detection === 1 ? '已启用' : '未启用'}
                     </Tag>
                   </Descriptions.Item>
                   
                   <Descriptions.Item label="样本库过滤" span={1}>
                     <Tag color={record.sample_library_filter === 1 ? 'cyan' : 'default'}>
                       {record.sample_library_filter === 1 ? '已启用' : '未启用'}
                     </Tag>
                   </Descriptions.Item>
                   <Descriptions.Item label="SYN Flood防护" span={1}>
                     <Tag color={record.syn_flood === 1 ? 'volcano' : 'default'}>
                       {record.syn_flood === 1 ? '已启用' : '未启用'}
                     </Tag>
                   </Descriptions.Item>
                   <Descriptions.Item label="域名过滤插件" span={1}>
                     <Tag color={record.domainfilter === 1 ? 'geekblue' : 'default'}>
                       {record.domainfilter === 1 ? '已启用' : '未启用'}
                     </Tag>
                   </Descriptions.Item>
                   
                   {/* SYN算法模式 */}
                   <Descriptions.Item label="SYN算法模式" span={3}>
                     <div>
                       <Tag color={record.syn_algorithm_one === 1 ? 'processing' : 'default'}>
                         模式一 {record.syn_algorithm_one === 1 ? '✓' : '✗'}
                       </Tag>
                       <Tag color={record.syn_algorithm_two === 1 ? 'processing' : 'default'}>
                         模式二 {record.syn_algorithm_two === 1 ? '✓' : '✗'}
                       </Tag>
                       <Tag color={record.syn_algorithm_three === 1 ? 'processing' : 'default'}>
                         模式三 {record.syn_algorithm_three === 1 ? '✓' : '✗'}
                       </Tag>
                     </div>
                   </Descriptions.Item>
                   
                   {/* 流量限制详情 */}
                   <Descriptions.Item label="入站流量限制" span={1}>
                     <Text>
                       {record.traffic_input_limit_per_second === -1 ? '使用系统流量限制' : 
                        record.traffic_input_limit_per_second === 0 ? '不限制' : 
                        `${record.traffic_input_limit_per_second} Mbps`}
                     </Text>
                   </Descriptions.Item>
                   <Descriptions.Item label="出站流量限制" span={2}>
                     <Text>
                       {record.traffic_output_limit_per_second === -1 ? '使用系统流量限制' : 
                        record.traffic_output_limit_per_second === 0 ? '不限制' : 
                        `${record.traffic_output_limit_per_second} Mbps`}
                     </Text>
                   </Descriptions.Item>
                 </Descriptions>
               </div>

               {/* 防护规则集详情 */}
               <div style={{ marginBottom: '16px' }}>
                 <Title level={5} style={{ marginBottom: '12px' }}>
                   防护规则集详情
                 </Title>
                 {renderDefendRuleSets(record.defend_rule_sets)}
               </div>
               
               {/* 全局触发规则 */}
               {record.global_trigger_rule && (
                 <div>
                   <Title level={5} style={{ marginBottom: '8px' }}>
                     全局触发规则
                   </Title>
                   <Descriptions size="small" bordered>
                     <Descriptions.Item label="规则名称">
                       {record.global_trigger_rule.rule_name}
                     </Descriptions.Item>
                     <Descriptions.Item label="规则ID">
                       <Text code>{record.global_trigger_rule.rule_id}</Text>
                     </Descriptions.Item>
                   </Descriptions>
                 </div>
               )}
             </div>
           ),
           rowExpandable: (record) => true,
         }}
      />
    );
  };

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: 'calc(100vh - 64px)' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <Title level={2} style={{ marginBottom: '24px' }}>
          <SettingOutlined style={{ marginRight: '8px' }} />
          IP应用规则查询
        </Title>

        {/* 查询区域 */}
        <Card style={{ marginBottom: '24px' }}>
          <Row gutter={[16, 16]} align="middle">
            <Col span={24}>
              <Space align="center" wrap>
                <Text strong>查询类型：</Text>
                <Radio.Group 
                  value={queryType} 
                  onChange={handleQueryTypeChange}
                  buttonStyle="solid"
                >
                  <Radio.Button value="firewall">
                    <SettingOutlined style={{ marginRight: '4px' }} />
                    大墙应用规则
                  </Radio.Button>
                  <Radio.Button value="cc">
                    <SettingOutlined style={{ marginRight: '4px' }} />
                    CC墙应用规则
                  </Radio.Button>
                </Radio.Group>
              </Space>
            </Col>
            <Col span={24}>
              <Space align="center" wrap>
                <Text strong>起始IP地址：</Text>
                <Input
                  placeholder="请输入起始IP地址（例：192.168.1.1）"
                  style={{ width: '300px' }}
                  value={startIp}
                  onChange={handleIpChange}
                  onPressEnter={handleQueryApplyRuleConfig}
                  prefix={<SearchOutlined />}
                />
                <Button
                  type="primary"
                  onClick={handleQueryApplyRuleConfig}
                  loading={loading}
                  disabled={!startIp.trim()}
                  icon={<SearchOutlined />}
                >
                  查询{queryType === 'cc' ? 'CC墙' : '大墙'}应用规则
                </Button>
                <Button
                  onClick={handleReset}
                  disabled={!hasQueried}
                  icon={<ReloadOutlined />}
                >
                  重置
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* 结果显示区域 */}
        {hasQueried && (
          <Card>
            <div style={{ marginBottom: '16px' }}>
              <Title level={4} style={{ margin: 0 }}>
                {queryType === 'cc' ? 'CC墙' : '大墙'}应用规则配置 {startIp && `- ${startIp}`}
              </Title>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
                <div style={{ marginTop: '16px' }}>
                  <Text type="secondary">正在查询{queryType === 'cc' ? 'CC墙' : '大墙'}应用规则配置...</Text>
                </div>
              </div>
            ) : applyRuleConfig ? (
              <div>
                {/* 基本信息 */}
                {applyRuleConfig.rule_info && applyRuleConfig.rule_info.length > 0 && (
                  <Card 
                    size="small" 
                    title={<><InfoCircleOutlined /> 基本信息</>}
                    style={{ marginBottom: '16px' }}
                  >
                    <Descriptions bordered size="small" column={3}>
                      <Descriptions.Item label="规则总数">
                        <Text strong>{applyRuleConfig.rule_info.length}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="查询IP">
                        <Text code>{startIp}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="查询类型">
                        <Tag color="processing">{queryType === 'cc' ? 'CC墙应用规则' : '大墙应用规则'}</Tag>
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                )}

                {/* 应用规则列表 */}
                <Card 
                  size="small" 
                  title={<><SettingOutlined /> 应用规则列表</>}
                >
                  {renderApplyRuleList(applyRuleConfig.rule_info || applyRuleConfig.rules || applyRuleConfig.apply_rules)}
                </Card>
              </div>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span>
                    暂无{queryType === 'cc' ? 'CC墙' : '大墙'}应用规则配置数据
                    <br />
                    <Text type="secondary">该IP暂未配置{queryType === 'cc' ? 'CC墙' : '大墙'}应用规则</Text>
                  </span>
                }
              />
            )}
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminApplyRuleQuery; 